import React, { useState, useEffect } from 'react';

export const FESTSETZUNG_COACH_OPTIONS = ['Christian', 'Martina', 'Jean', 'Nelly'] as const;

interface PasswordPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string, coachName?: string) => void;
  title?: string;
  /** Wenn true: Coach-Dropdown anzeigen und Auswahl verlangen (nur beim Festsetzen). */
  requireCoachSelect?: boolean;
  /** Passwort überspringen (Session schon freigeschaltet, nur noch Coach wählen). */
  skipPassword?: boolean;
  coachOptions?: readonly string[];
}

const PasswordPrompt: React.FC<PasswordPromptProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Passwort eingeben',
  requireCoachSelect = false,
  skipPassword = false,
  coachOptions = FESTSETZUNG_COACH_OPTIONS,
}) => {
  const [password, setPassword] = useState('');
  const [coachName, setCoachName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setCoachName('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const canConfirm =
    (skipPassword || password.length > 0) && (!requireCoachSelect || coachName.length > 0);

  const handleConfirm = () => {
    if (!canConfirm) {
      if (requireCoachSelect && !coachName) {
        alert('Bitte Coach auswählen.');
      }
      return;
    }
    onConfirm(skipPassword ? '90' : password, requireCoachSelect ? coachName : undefined);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 transition-all duration-300">
      <div className="bg-slate-900 border border-slate-700 p-8 rounded-xl shadow-2xl w-full max-w-sm transform transition-all">
        <h2 className="text-xl font-bold text-white mb-6 text-center font-sans tracking-wide">
          {title}
        </h2>

        {requireCoachSelect && (
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-2 font-sans">
              Festgesetzt von
            </label>
            <select
              value={coachName}
              onChange={(e) => setCoachName(e.target.value)}
              className="w-full p-3 rounded-md bg-black text-white font-sans text-base border border-slate-600 focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none transition-colors"
              autoFocus
            >
              <option value="">Coach wählen…</option>
              {coachOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}

        {!skipPassword && (
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="••••"
            className="w-full p-3 rounded-md bg-black text-white text-center font-mono text-lg font-bold border border-slate-600 focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none transition-colors placeholder-slate-700"
            autoFocus={!requireCoachSelect}
          />
        )}

        <div className="flex justify-between mt-8 space-x-4">
          <button
            onClick={onClose}
            className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-bold py-2 px-4 rounded-md transition-colors font-sans"
          >
            Abbrechen
          </button>

          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 bg-[#E6C299] hover:bg-[#d4b088] text-black border border-[#E6C299] font-bold py-2 px-4 rounded-md transition-colors font-sans shadow-[0_0_15px_rgba(230,194,153,0.1)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#E6C299]"
          >
            Bestätigen
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordPrompt;
